import React, { useState, useEffect, useRef } from 'react';
import { Trophy, Star, Award, Crown } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

const XpLevelDisplay = ({ levelInfo }) => {
    const [animate, setAnimate] = useState(false);
    const [prevXp, setPrevXp] = useState(levelInfo?.total_xp || 0);
    const progressRef = useRef(null);
    const isInView = useInView(progressRef, { 
        once: true,
        amount: 1  // Triggers when 100% of the element is visible
    });

    // Trigger animation when XP changes
    useEffect(() => {
        if (levelInfo?.total_xp !== prevXp) {
            setAnimate(true);
            const timer = setTimeout(() => setAnimate(false), 1500);
            setPrevXp(levelInfo?.total_xp);
            return () => clearTimeout(timer);
        }
    }, [levelInfo?.total_xp, prevXp]);

    const getLevelIcon = (levelName) => {
        switch (levelName) {
            case 'Rookie':
                return <Star className="w-6 h-6 text-blue-500" />;
            case 'Pro':
                return <Trophy className="w-6 h-6 text-green-500" />;
            case 'All-Star':
                return <Award className="w-6 h-6 text-purple-500" />;
            case 'Legend':
                return <Crown className="w-6 h-6 text-yellow-500" />;
            default:
                return <Star className="w-6 h-6 text-blue-500" />;
        }
    };

    if (!levelInfo) return null;

    return (
        <div className="bg-white rounded-lg p-6 shadow-sm border-2 border-popashot-grey-light">
            <motion.div 
                className="space-y-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                {/* Level and XP Display */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {getLevelIcon(levelInfo.current_level)}
                            <h3 className="text-xl font-extrabold">{levelInfo.current_level}</h3>
                    </div>
                    <motion.span 
                        className={`text-xl font-extrabold ${animate ? 'text-green-500' : 'text-popashot-red'}`}
                        initial={{ opacity: 0 }}
                        animate={{ 
                            opacity: 1,
                            y: animate ? [-20, 0] : 0
                        }}
                        transition={{ duration: 0.5 }}
                    >
                        {levelInfo.total_xp} XP
                    </motion.span>
                </div>

                {/* Progress Bar */}
                <div className="relative pt-1">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-gray-600">
                            {levelInfo.next_level ? (
                                <>Next Level: {levelInfo.next_level}</>
                            ) : (
                                'Max Level Reached!'
                            )}
                        </div>
                        {levelInfo.next_level && (
                            <div className="text-xs text-gray-600">
                                {levelInfo.xp_needed} XP needed
                            </div>
                        )}
                    </div>
                    <div ref={progressRef} className="overflow-hidden h-2 text-xs flex rounded bg-yellow-100">
                        <motion.div
                            initial={{ width: "0%" }}
                            animate={{ width: isInView ? `${levelInfo.progress_percent}%` : "0%" }}
                            transition={{ 
                                duration: 1, 
                                ease: "easeOut",
                                delay: 0.5  // Add 0.5 second delay before starting animation
                            }}
                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-yellow-500"
                        />
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default XpLevelDisplay;
